import { Component, EventEmitter, Input, Output, ChangeDetectionStrategy } from '@angular/core';

import { TranslatePipe } from '@ngx-translate/core';
import { SharedModule } from '../../../@commons/ui-components/shared.module';
import { BaseToastComponent } from '../../../@commons/ui-components/toast-popup/base-toast-component';
import { ChoiceOption } from '../../../@commons/ui-components/choice-selector/choice-selector.component';
import { DatePickerComponent } from '../../../@commons/@components/date-picker/date-picker.component';
import { StorageService } from '../../../@commons/@services/front/storage.service';
import { PosthogService } from '../../../@commons/@services/front/posthog.service';
import { PlantApi } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/sdk/location/plant/plant-api';
import { PlantEndReason } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@enums/plant-end-reason.enum';
import { LabelDto } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@dto/label-dto';
import { HarvestPlantsResponse } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@dto/responses/location/harvest-plants.response';

/** Ce que la feuille a besoin de savoir d'un plant : son uid, sa cohorte et son nom. */
export interface EndablePlant {
  uid: string;
  nbPlants?: number;
  cultivationVariant?: { name?: LabelDto | string };
}

/**
 * Retirer une ou plusieurs cultures (#206, PC-06) : clôture le cycle de vie (récoltée /
 * gelée / maladie / arrachée / montée en graines). Les plantes sont archivées, quittent les
 * vues actives et libèrent leur case ; elles restent dans l'historique. À NE PAS confondre
 * avec « Récolter » (journal, la plante continue) ni avec « Supprimer définitivement ».
 *
 * Un seul plant passe par `harvest`, plusieurs par `harvestMany` : le lot n'est pas
 * transactionnel, `ended` transmet ce qui a été retiré et ce qui reste à réessayer.
 */
import { LabelPipe } from '../../../@commons/pipes/LabelPipe';
@Component({
  selector: 'vw-end-culture-toast',
  imports: [LabelPipe, SharedModule, TranslatePipe, DatePickerComponent],
  providers: [{ provide: PlantApi, useClass: PlantApi }],
  templateUrl: './end-culture-toast.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './end-culture-toast.component.scss',
})
export class EndCultureToastComponent extends BaseToastComponent {
  @Input() plants: EndablePlant[] = [];
  @Output() ended = new EventEmitter<HarvestPlantsResponse>();

  reason: PlantEndReason | null = null;
  date: Date = new Date();
  submitLoading = false;
  /** L'appel a échoué en entier : rien n'a été retiré, la feuille reste ouverte. */
  submitFailed = false;
  /** « Retirer » touché sans motif : la feuille dit ce qui manque (bouton jamais grisé). */
  reasonMissing = false;
  /** Ordre de la maquette 05 : la gelée juste après la récolte, c'est la saison. */
  readonly reasonOptions: ChoiceOption[] = [
    PlantEndReason.HARVESTED,
    PlantEndReason.FROZEN,
    PlantEndReason.DIED,
    PlantEndReason.REMOVED,
    PlantEndReason.SEEDS,
  ].map((value) => ({ value, label: 'garden.plant.end.' + value }));

  constructor(
    private plantApi: PlantApi,
    private storageService: StorageService,
    private posthog: PosthogService,
  ) {
    super();
  }

  /** Nombre de plants réellement retirés, pas de cartes : une carte « x3 plants » en compte 3. */
  get plantCount(): number {
    return this.plants.reduce((sum, p) => sum + (p.nbPlants ?? 1), 0);
  }

  get isSinglePlant(): boolean {
    return this.plantCount === 1;
  }

  onDateChange(date: Date): void {
    this.date = date;
  }

  onReasonChange(value: PlantEndReason | null): void {
    this.reason = value;
    if (value) this.reasonMissing = false;
  }

  async submit(): Promise<void> {
    if (this.submitLoading || !this.plants.length) return;
    if (!this.reason) {
      this.reasonMissing = true;
      return;
    }
    this.submitLoading = true;
    this.submitFailed = false;
    const localDate = new Date(
      this.date.getTime() - this.date.getTimezoneOffset() * 60000,
    )
      .toISOString()
      .split('T')[0];
    const body = { endReason: this.reason, endedAt: localDate };
    const lang = this.storageService.getCurrentLanguage();
    try {
      let result: HarvestPlantsResponse;
      if (this.plants.length === 1) {
        await this.plantApi.harvest(this.plants[0].uid, body, lang);
        result = { archivedUids: [this.plants[0].uid], failedUids: [] };
      } else {
        result = await this.plantApi.harvestMany(
          { ...body, uids: this.plants.map((p) => p.uid) },
          lang,
        );
      }
      for (const plantUid of result.archivedUids) {
        this.posthog.capture('plant_ended', { plantUid, endReason: this.reason });
      }
      this.ended.emit(result);
      this.onClose();
    } catch {
      this.submitFailed = true;
    } finally {
      this.submitLoading = false;
    }
  }
}
